"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { DB_PLAN_RANK, dbPlanToResolved } from "@/lib/stripe/plans";

/**
 * Client-side hook that resolves the user's current entitlement.
 *
 * Returns the BEST active subscription (by tier rank), plus a free-tier
 * score counter and helpers used by the paywall.
 *
 * The hook exposes:
 *   • plan                — "free" | "pass" | "pro" | "studio" | null
 *   • validUntil          — when the plan expires (null for free / null user)
 *   • freeScoresUsed      — count of distinct plots this user has scored
 *   • freeScoresRemaining — 3 - used, capped at 0. Null when paid (unlimited).
 *   • scoredPandIds       — Set of BAG pand ids this user has already scored.
 *                           Lets already-scored plots be revealed freely on
 *                           return visits without using another free credit.
 *   • canScorePlot(pand)  — true when viewing a score for this pand is allowed
 *   • logScoreView(pand)  — idempotently records a score view for this pand
 *   • refresh()           — re-read entitlement state from Supabase
 */

export type ResolvedPlan =
  | null           // not signed in
  | "free"         // signed in, no active subscription
  | "pass"         // Buyer's Pass
  | "pro"          // Pro monthly or annual (same tier)
  | "studio";      // Studio monthly or annual (same tier)

export interface Entitlement {
  plan: ResolvedPlan;
  validUntil: Date | null;
  freeScoresUsed: number;
  freeScoresRemaining: number | null;
  scoredPandIds: Set<string>;
  loading: boolean;
  /** Non-null when the most recent fetch failed. The hook keeps the last
   *  successful values so the UI doesn't go blank, but consumers can show
   *  a banner / retry CTA when this is set. */
  error: string | null;
  /**
   * Instant client-side guess at whether scoring this plot is allowed.
   * Useful for rendering the paywall optimistically before any network
   * round-trip — the SERVER's authorizeScore() call below is the actual
   * source of truth. Treat this as a UX hint.
   */
  canScorePlot: (pandId: string | null | undefined) => boolean;
  /**
   * Server-authoritative gate. Call this before kicking off enrichment
   * for a clicked plot. Returns once the server has atomically checked
   * the user's quota and (if allowed) logged the view in usage_events.
   *
   * Resolves with `{ authorized: false }` for blocked plots — caller
   * must then render the SoftWall and skip enrichment.
   */
  authorizeScore: (pandId: string | null | undefined) => Promise<AuthorizeResult>;
  /**
   * Fire-and-forget: attach the resolved address to this user's score event
   * for `pandId` so the dashboard's Recent Activity can name a SCORED (not just
   * saved) plot. Idempotent — once per pand per session, and the RPC only fills
   * the label while it's still null. No-op for anonymous callers / unscored plots.
   */
  labelScore: (
    pandId: string | null | undefined,
    label: string | null | undefined,
    extras?: ScoreEventExtras,
  ) => void;
  /**
   * Fire-and-forget: record that a PDF dossier was built for `pandId`, so the
   * dashboard's Recent Activity and the billing page's Usage row can count it.
   * Nothing wrote this event before 2026-09-10 although both surfaces waited
   * for it. Never gates anything; the quota counts score_view only.
   */
  logReportDownload: (
    pandId: string | null | undefined,
    label: string | null | undefined,
    extras?: ScoreEventExtras,
  ) => void;
  refresh: () => void;
}

/**
 * What a score event remembers beyond the pand id and the address: the centre
 * the cascade resolved (so /app?select=<lat>,<lng> can reopen an UNSAVED
 * property from the dashboard) and the composite it produced (so the activity
 * row can wear a score chip). All optional; the RPC ignores what it is not
 * given and never overwrites what is already there.
 */
export interface ScoreEventExtras {
  lat?: number | null;
  lng?: number | null;
  score?: number | null;
}

export interface AuthorizeResult {
  authorized: boolean;
  alreadyScored?: boolean;
  remaining?: number | null;
  /** Set when authorized=false. */
  reason?: "free_quota_exceeded" | "not_signed_in" | "missing_pand_id" | "rpc_missing" | "rpc_error" | "network_error";
}

export const FREE_SCORE_QUOTA = 3;

/** The optional RPC arguments for a score event, dropped when absent so an
 *  older function signature (pre usage_events_history.sql) still matches. */
function rpcExtras(extras?: ScoreEventExtras): Record<string, number> {
  const out: Record<string, number> = {};
  if (typeof extras?.lat === "number" && typeof extras?.lng === "number"
      && Number.isFinite(extras.lat) && Number.isFinite(extras.lng)) {
    out.p_lat = extras.lat;
    out.p_lng = extras.lng;
  }
  if (typeof extras?.score === "number" && Number.isFinite(extras.score)) {
    out.p_score = Math.round(extras.score);
  }
  return out;
}

export function useEntitlement(): Entitlement {
  const { user, loading: authLoading, configured } = useAuth();
  const [plan, setPlan] = useState<ResolvedPlan>(null);
  const [validUntil, setValidUntil] = useState<Date | null>(null);
  const [scoredPandIds, setScoredPandIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Track logging in progress so logScoreView is idempotent even under
  // rapid-fire effect re-runs (React strict-mode double-invoke, etc.)
  const loggingInFlight = useRef<Set<string>>(new Set());

  const refresh = useCallback(() => setRefreshCounter(n => n + 1), []);

  // Dep on user.id (string), NOT the user object reference. Supabase emits a
  // fresh User object on every token refresh (~every 60 min by default, but
  // also on tab focus), which would re-fire this effect and overwrite the
  // local optimistic scoredPandIds with whatever's currently in the DB —
  // making the paywall "flicker on and off" as the count was repeatedly
  // reset between actual scoring activity. The id is stable across refreshes
  // and only changes when the user genuinely changes.
  const userId = user?.id ?? null;

  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      setPlan(null);
      setValidUntil(null);
      setScoredPandIds(new Set());
      setLoading(false);
      return;
    }

    const supabase = createClient();
    if (!supabase) {
      setPlan("free");
      setScoredPandIds(new Set());
      setLoading(false);
      return;
    }

    let canceled = false;
    (async () => {
      setLoading(true);

      const nowIso = new Date().toISOString();

      // 1. Best active subscription (higher tier wins; then longer validity)
      const subsResult = await supabase
        .from("subscriptions")
        .select("plan, status, current_period_end")
        .eq("user_id", userId)
        .eq("status", "active")
        .gt("current_period_end", nowIso);

      if (canceled) return;

      // Surface query failures so a transient Supabase outage doesn't quietly
      // demote a paid user to "free". Keep the previous plan in state until
      // the next successful refresh; consumers can read `error` to show a
      // banner.
      if (subsResult.error) {
        console.error("[useEntitlement] subscriptions query failed:", subsResult.error.message);
        setError(subsResult.error.message);
        setLoading(false);
        return;
      }

      const subs = subsResult.data;
      let bestPlan: ResolvedPlan = "free";
      let bestValidUntil: Date | null = null;
      if (subs && subs.length > 0) {
        const top = [...subs].sort((a, b) => {
          const rankA = DB_PLAN_RANK[a.plan as keyof typeof DB_PLAN_RANK] ?? 0;
          const rankB = DB_PLAN_RANK[b.plan as keyof typeof DB_PLAN_RANK] ?? 0;
          if (rankA !== rankB) return rankB - rankA;
          return new Date(b.current_period_end).getTime() - new Date(a.current_period_end).getTime();
        })[0];
        bestPlan = dbPlanToResolved(top.plan);
        bestValidUntil = new Date(top.current_period_end);
      }

      // 2. All distinct pand ids this user has already scored.
      // For paid users this isn't gating-critical but it's small data and
      // it's nice for analytics + future "scored plots" views.
      const eventsResult = await supabase
        .from("usage_events")
        .select("subject_ref")
        .eq("user_id", userId)
        .eq("kind", "score_view")
        .not("subject_ref", "is", null);

      if (canceled) return;

      // Failure here is less severe — the worst case is the user's free-quota
      // counter is wrong for this session. Log + surface but still apply the
      // subscription result we already resolved above.
      if (eventsResult.error) {
        console.error("[useEntitlement] usage_events query failed:", eventsResult.error.message);
        setError(eventsResult.error.message);
      } else {
        setError(null);
      }

      const events = eventsResult.data;
      const dbIds = new Set<string>();
      for (const e of events ?? []) {
        if (e.subject_ref) dbIds.add(e.subject_ref as string);
      }

      setPlan(bestPlan);
      setValidUntil(bestValidUntil);
      // MERGE rather than overwrite: keep any optimistic adds that haven't
      // been read back from the DB yet (they will be on the next legitimate
      // re-run, since the same user.id is still in scope). This cooperates
      // with the no-rollback policy in logScoreView so the counter only ever
      // grows during a session and a transient persistence failure can't
      // open the gate.
      setScoredPandIds(prev => {
        const merged = new Set(prev);
        dbIds.forEach(id => merged.add(id));
        return merged;
      });
      setLoading(false);
    })();

    return () => { canceled = true; };
  }, [userId, authLoading, refreshCounter]);

  const isPaid = plan === "pass" || plan === "pro" || plan === "studio";
  const freeScoresUsed = scoredPandIds.size;
  const freeScoresRemaining =
    plan === "free"
      ? Math.max(0, FREE_SCORE_QUOTA - freeScoresUsed)
      : null;

  const canScorePlot = useCallback((pandId: string | null | undefined): boolean => {
    // Never gate unauthenticated callers through this hook — the middleware
    // / signin redirect handles them. If this runs with no user, default
    // allow so we don't lock out excluded / pre-auth contexts.
    if (!user) return true;
    if (isPaid) return true;
    if (!pandId) return true;  // can't gate without an id; fail-open
    if (scoredPandIds.has(pandId)) return true;  // previously scored, free to re-view
    return (freeScoresRemaining ?? 0) > 0;
  }, [user, isPaid, scoredPandIds, freeScoresRemaining]);

  const authorizeScore = useCallback(async (pandId: string | null | undefined): Promise<AuthorizeResult> => {
    // Without an auth backend (a keyless dev server, where middleware runs
    // anonymous) there is no quota to check. Everywhere else the SERVER
    // decides, from the session cookie, even while this hook has no user yet:
    // a scan can start before getSession() has answered (/app?select= fires
    // as the map becomes ready), and "no user yet" used to be waved through
    // as anonymous. A plot with no pand has nothing to count.
    if (!configured) return { authorized: true };
    if (!pandId) return { authorized: true };

    // Idempotency / debounce — if we already authorized this same pand
    // during this session, return immediately. The server is also idempotent
    // (re-views are free), but skipping the round-trip shaves latency on
    // re-clicks.
    if (scoredPandIds.has(pandId)) {
      return { authorized: true, alreadyScored: true };
    }
    if (loggingInFlight.current.has(pandId)) {
      // Another concurrent authorize call is in flight for the same pand.
      // Return optimistically allowed so we don't block the UI, but skip
      // the local set update until that one resolves.
      return { authorized: true };
    }

    loggingInFlight.current.add(pandId);

    try {
      const res = await fetch("/api/score/authorize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pandId }),
      });

      // 402 Payment Required is our paywall signal. The body still has the
      // structured payload — we read it for `remaining` and `reason`.
      if (res.status === 402) {
        const payload = (await res.json().catch(() => ({}))) as Partial<AuthorizeResult>;
        return {
          authorized: false,
          reason: payload.reason ?? "free_quota_exceeded",
          remaining: payload.remaining ?? 0,
        };
      }

      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as Partial<AuthorizeResult>;
        // 500 with rpc_missing means the SQL migration hasn't been run on
        // the target Supabase project. Surface that loudly — the dev needs
        // to run supabase/authorize_score_view.sql.
        if (payload.reason === "rpc_missing") {
          console.error(
            "[useEntitlement] /api/score/authorize: RPC missing. " +
            "Run supabase/authorize_score_view.sql in your Supabase SQL editor."
          );
        } else {
          console.error("[useEntitlement] authorize failed:", res.status, payload);
        }
        // Fail-CLOSED on transport errors: a broken auth pipe shouldn't
        // open the gate. Better to over-gate while we debug than to leak
        // free scores.
        return {
          authorized: false,
          reason: (payload.reason as AuthorizeResult["reason"]) ?? "rpc_error",
        };
      }

      const payload = (await res.json()) as AuthorizeResult & { plan?: string };
      // Update the local scoredPandIds so subsequent canScorePlot calls and
      // the freeScoresRemaining display reflect the just-authorized view.
      // Server is the source of truth, but we keep this in sync so the UI
      // doesn't have to wait for a re-fetch.
      setScoredPandIds(prev => {
        if (prev.has(pandId)) return prev;
        const next = new Set(prev);
        next.add(pandId);
        return next;
      });
      return payload;
    } catch (err) {
      console.error("[useEntitlement] authorize network error:", err);
      // Network failure → fail closed. Same rationale as above.
      return { authorized: false, reason: "network_error" };
    } finally {
      loggingInFlight.current.delete(pandId);
    }
  }, [configured, scoredPandIds]);

  // Once-per-pand-per-session guard so re-viewing a plot doesn't re-fire the
  // label write. The RPC is idempotent too (only sets subject_label when null).
  const labeledPands = useRef<Set<string>>(new Set());
  const labelScore = useCallback((
    pandId: string | null | undefined,
    label: string | null | undefined,
    extras?: ScoreEventExtras,
  ) => {
    if (!user || !pandId || !label) return;
    if (labeledPands.current.has(pandId)) return;
    labeledPands.current.add(pandId);
    const supabase = createClient();
    if (!supabase) { labeledPands.current.delete(pandId); return; }
    void supabase
      .rpc("label_score_view", { p_pand_id: pandId, p_label: label, ...rpcExtras(extras) })
      .then(({ error }) => {
        if (!error) return;
        // Allow a later retry on transient failure.
        labeledPands.current.delete(pandId);
        // A missing RPC just means the migration isn't applied on this env —
        // stay quiet rather than warning on every score.
        if (!/does not exist|schema cache|PGRST202|could not find/i.test(error.message ?? "")) {
          console.warn("[useEntitlement] label_score_view failed:", error.message);
        }
      });
  }, [user]);

  // One row per PDF built. Not deduped per session on purpose: two downloads
  // are two events, which is what "reports this period" should count.
  const logReportDownload = useCallback((
    pandId: string | null | undefined,
    label: string | null | undefined,
    extras?: ScoreEventExtras,
  ) => {
    if (!user) return;
    const supabase = createClient();
    if (!supabase) return;
    void supabase
      .rpc("log_report_download", {
        p_pand_id: pandId ?? null,
        p_label: label ?? null,
        ...rpcExtras(extras),
      })
      .then(({ error }) => {
        if (!error) return;
        if (!/does not exist|schema cache|PGRST202|could not find/i.test(error.message ?? "")) {
          console.warn("[useEntitlement] log_report_download failed:", error.message);
        }
      });
  }, [user]);

  return useMemo(
    () => ({
      plan,
      validUntil,
      freeScoresUsed,
      freeScoresRemaining,
      scoredPandIds,
      loading: authLoading || loading,
      error,
      canScorePlot,
      authorizeScore,
      labelScore,
      logReportDownload,
      refresh,
    }),
    [plan, validUntil, freeScoresUsed, freeScoresRemaining, scoredPandIds, authLoading, loading, error, canScorePlot, authorizeScore, labelScore, logReportDownload, refresh]
  );
}

// dbPlanToResolved is imported from @/lib/stripe/plans — single source of
// truth shared with the server-side /api/entitlement/check route so the
// two paths cannot drift apart when a new plan tier is added.
